// 📄 hooks/useFavorites.ts
// Favoritos del proyecto para el usuario actual — un solo hook por página
// (ProjectSolutionsHome, HerramientasHome, ...), no uno por card: así
// todas las estrellas de una misma página comparten el mismo estado y se
// mantienen sincronizadas entre sí al togglear cualquiera.
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext.tsx';
import { FavoriteItemType, FavoriteMetadata, addFavorite, getFavorites, removeFavorite } from '../services/favoriteService.ts';

export function useFavorites(projectId: string) {
  const { user } = useAuth();
  const uid = user?.uid;
  const [favorites, setFavorites] = useState<FavoriteMetadata[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid || !projectId) {
      setLoading(false);
      return;
    }

    let cancelado = false;
    setLoading(true);
    getFavorites(uid, projectId)
      .then((list) => { if (!cancelado) setFavorites(list); })
      .catch((err) => console.error('Error cargando favoritos:', err))
      .finally(() => { if (!cancelado) setLoading(false); });

    return () => {
      cancelado = true;
    };
  }, [uid, projectId]);

  const isFavorite = (itemType: FavoriteItemType, itemId: string): boolean =>
    favorites.some((f) => f.itemType === itemType && f.itemId === itemId);

  const toggleFavorite = async (itemType: FavoriteItemType, itemId: string): Promise<void> => {
    if (!uid || !projectId) return;

    const previo = favorites;
    const yaEsFavorito = isFavorite(itemType, itemId);

    // Optimistic update — revert si el write falla (ver catch).
    setFavorites((prev) =>
      yaEsFavorito
        ? prev.filter((f) => !(f.itemType === itemType && f.itemId === itemId))
        : [{ itemType, itemId, addedAt: null }, ...prev]
    );

    try {
      if (yaEsFavorito) {
        await removeFavorite(uid, projectId, itemType, itemId);
      } else {
        await addFavorite(uid, projectId, itemType, itemId);
      }
    } catch (err) {
      console.error('Error actualizando favorito:', err);
      setFavorites(previo);
    }
  };

  return { favorites, loading, isFavorite, toggleFavorite };
}
